import { BookingPolicyDto } from '../../types/shared-types';
import { Service } from '@wix/ambassador-bookings-services-v2-service/types';
import { BreadcrumbsViewModel } from '../breadcrumbs-view-model/breadcrumbsViewModel';
import { Breadcrumbs } from 'wix-ui-tpa';
import { getBookOnlineFullUrl } from '@wix/bookings-catalog-calendar-viewer-navigation';
import { IWixAPI } from '@wix/yoshi-flow-editor';

export interface BodyViewModel {
  messageType?: AVAILABILITY_MESSAGE_TYPE;
  isBookable: boolean;
  timeUntilServiceIsOpen: string;
  /** The calendar date enrollment opens. Absent when the policy names none. */
  enrollmentOpenDate?: string;
  serviceName: string;
  breadcrumbsViewModel?: BreadcrumbsViewModel;
}

// https://stackoverflow.com/questions/63961803/eslint-says-all-enums-in-typescript-app-are-already-declared-in-the-upper-scope
// eslint-disable-next-line no-shadow
export enum AVAILABILITY_MESSAGE_TYPE {
  FULLY_BOOKED = 'FULLY_BOOKED',
  TOO_EARLY = 'TOO_EARLY',
  TOO_LATE = 'TOO_LATE',
  STARTED_AND_BOOKABLE = 'STARTED_AND_BOOKABLE',
  NOT_AVAILABLE = 'NOT_AVAILABLE',
}

const getServiceAvailability = ({
  isFullyBooked,
  isServiceStartedAndBookable,
  isTooLateToBook,
  isServiceAvailable,
  isTooEarlyToBook,
  isBookable,
}: BookingPolicyDto) => {
  if (isFullyBooked || !isServiceAvailable || isTooEarlyToBook) {
    return false;
  }
  if (isTooLateToBook) {
    return isServiceStartedAndBookable;
  }
  return isBookable;
};

/**
 * The moment enrollment opens, as a calendar date the visitor can read.
 * It has to match the course date range beside it, so it uses the same two
 * inputs: the site's regional DATE locale, not its language, and the timezone
 * currently selected in the view. Language alone gives US ordering to an
 * en-GB site, and the runtime timezone can name the wrong day near midnight.
 */
const formatEnrollmentOpenDate = (
  enrollmentOpensAt?: string,
  dateRegionalSettingsLocale = 'en-US',
  viewTimezone?: string,
) => {
  if (!enrollmentOpensAt) {
    return undefined;
  }
  const opensAt = new Date(enrollmentOpensAt);
  if (Number.isNaN(opensAt.valueOf())) {
    return undefined;
  }

  return new Intl.DateTimeFormat(dateRegionalSettingsLocale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: viewTimezone,
  }).format(opensAt);
};

export const bodyViewModelFactory = ({
  bookingsPolicy,
  service,
  dateRegionalSettingsLocale,
  viewTimezone,
}: {
  bookingsPolicy: BookingPolicyDto;
  service: Service;
  dateRegionalSettingsLocale?: string;
  viewTimezone?: string;
}): BodyViewModel => {
  const messageType = getAvailabilityMessageType(bookingsPolicy);
  const isAvailableService = getServiceAvailability(bookingsPolicy);
  return {
    messageType,
    isBookable: isAvailableService,
    timeUntilServiceIsOpen: bookingsPolicy.timeUntilServiceIsOpen,
    enrollmentOpenDate: formatEnrollmentOpenDate(
      bookingsPolicy.enrollmentOpensAt,
      dateRegionalSettingsLocale,
      viewTimezone,
    ),
    serviceName: service.name!,
  };
};

const getAvailabilityMessageType = (
  bookingPolicyDto: BookingPolicyDto,
): AVAILABILITY_MESSAGE_TYPE | undefined => {
  if (bookingPolicyDto.isBookable) {
    if (bookingPolicyDto.isFullyBooked) {
      return AVAILABILITY_MESSAGE_TYPE.FULLY_BOOKED;
    } else if (bookingPolicyDto.isTooEarlyToBook) {
      return AVAILABILITY_MESSAGE_TYPE.TOO_EARLY;
    } else if (bookingPolicyDto.isTooLateToBook) {
      return AVAILABILITY_MESSAGE_TYPE.TOO_LATE;
    } else if (!bookingPolicyDto.isServiceAvailable) {
      return AVAILABILITY_MESSAGE_TYPE.NOT_AVAILABLE;
    } else if (bookingPolicyDto.isServiceStartedAndBookable) {
      return AVAILABILITY_MESSAGE_TYPE.STARTED_AND_BOOKABLE;
    }
  }
};
